import { faFileEdit as faFileEditRegular, faSoup } from "@fortawesome/pro-regular-svg-icons";
import { faCaretDown, faFileEdit as faFileEditSolid } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { formValueSelector, getFormValues } from "redux-form";

import {
    ARTICLE_CREATE_EDIT_FORM_NAME,
    ArticleFormData,
} from "@/mk2/apps/blogs/components/ArticleCreateEditForm";
import {
    clearPastPublishTime,
    getArticleTitleAsString,
} from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import ArticleEdit from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit";
import {
    ARTICLE_EDIT_SAVE_ARTICLE_TRIGGER,
    articleEditChangeEditorTypeTrigger,
    articleEditFetchTrigger,
    articleEditInsertImageTrigger,
    articleEditSaveArticleTrigger,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.actions";
import {
    ArticleEditSaveActionType,
    BLOGS_ARTICLE_EDIT_MENU_MORE,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.constants";
import {
    blogArticleAstToHTML,
    blogArticleAstToMarkdown,
    prepareHTMLArticleForFroala,
    prepareMarkdownArticleForFroala,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.helpers";
import reducer, {
    ArticleEditState,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.reducers";
import saga from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.sagas";
import { getArticleEditState } from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.selectors";
import { articleBlogsOG } from "@/mk2/apps/blogs/structuredData";
import {
    blogsArticleUrl,
    editAdvancedArticleDetailsUrl,
    editArticleBodyUrl,
} from "@/mk2/apps/blogs/urls";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { SmartTextareaEditorType } from "@/mk2/components/forms/SmartTextareaField";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { FormPageType } from "@/mk2/constants/enums";
import { AppName } from "@/mk2/containers/Application/Application";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { PhotosUploadConfig } from "@/mk2/containers/PhotosUpload/PhotosUpload";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { getAllDescendantsOfTypes } from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { cachedInitialValues } from "@/mk2/helpers/form";
import { PhotoUploadStatus } from "@/mk2/helpers/form.reducers";
import { photoEntitiesToSuccessfulPhotoUploads } from "@/mk2/helpers/photos";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { BlogPostEntity, BlogPostSchema, UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SAVE_AS,
    SAVE_AS_DRAFT,
    SAVE_AS_PUBLISHED,
    SEO_TITLE_BLOGS_article,
} from "@/mk/autogenerated/translations/ArticleEditPage.4f3b231b5594b1202692d349789e6694";
import { IS_PROD } from "@/mk/settings";

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    loadingState: LoadingState;
    articlePost: BlogPostEntity;
    showAds: boolean;

    requestUser: UserEntity;
    requestUserPermissions: string[];
    baseUrl: string;
    isMobile: boolean;
    isUploadingPhotos: boolean;
    isConvertingBody: boolean;
    isCreatingDraft: boolean;
    disableEditorTypeChange: boolean;
    initialValues: ArticleFormData;
    currentValues: ArticleFormData;
}

interface DispatchProps extends FormPageDispatchProps {
    onLoad(username: string, articleSlug: string);
    onEditorTypeChange(username: string, articleSlug: string, editorType: SmartTextareaEditorType);
    onSaveAsDraft(
        username: string,
        articleSlug: string,
        articleId: number,
        articleFd: ArticleFormData,
    );
    onSaveAndPublish(
        username: string,
        articleSlug: string,
        articleId: number,
        articleFd: ArticleFormData,
    );
    onPrepareUploadPhotosCustom?(
        editor: any,
        formName: string,
        config: PhotosUploadConfig,
        files: File[],
        processPhotoUploadOnFormPage: FormPageType,
        accessKey?: string,
    );
    onArticleEditToggleMenuMore();
}

type RouteProps = PageRouteProps<{
    username: string;
    articleSlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticleEditPage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private useOnlyMarkdownEditorCache = cacheLast(true);
    private astToMarkdownCache = cacheLast(true);
    private astToHtmlCache = cacheLast(true);

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const { username, articleSlug } = ownProps.routeParams;

            const articleEditState: ArticleEditState = getArticleEditState(
                state,
                username,
                articleSlug,
            );
            const articlePost: BlogPostEntity = getDenormalizedEntity<BlogPostEntity>(
                state,
                BlogPostSchema,
                articleEditState.post,
            );
            const showAds =
                articlePost &&
                !articlePost.isPRArticle &&
                !articlePost.isActiveAmbassador &&
                !articlePost.isNoAds;

            const currentValues = getFormValues(ARTICLE_CREATE_EDIT_FORM_NAME)(
                state,
            ) as ArticleFormData;

            const selector = formValueSelector(ARTICLE_CREATE_EDIT_FORM_NAME);
            const currentUploads = (selector(state, "photos") as any[]) || [];
            const isFormUploadingPhotos: boolean = currentUploads
                ? currentUploads.findIndex((u) => u.status === PhotoUploadStatus.PENDING) >= 0
                : false;

            const useOnlyMarkdownEditor = articlePost
                ? this.useOnlyMarkdownEditorCache(
                      () => {
                          const elementsNotSupportedByRichtextEditor = getAllDescendantsOfTypes(
                              articlePost.contentAST,
                              [
                                  "reviews",
                                  "previews",
                                  "testing",
                                  "market",
                                  "strollers_chart",
                                  "iframe",
                                  "pricemania",
                                  "product_box",
                                  "pregnancy_newsletter",
                                  "pregnancy_calculator",
                                  "eshop",
                                  "prima",
                              ],
                          );
                          return elementsNotSupportedByRichtextEditor.length > 0;
                      },
                      username,
                      articleSlug,
                  )
                : true;
            const initialEditorType = !useOnlyMarkdownEditor
                ? SmartTextareaEditorType.RICHTEXT
                : SmartTextareaEditorType.MARKDOWN;
            const articleContent =
                articlePost && articlePost.contentAST
                    ? initialEditorType === SmartTextareaEditorType.MARKDOWN
                        ? this.astToMarkdownCache(
                              () =>
                                  prepareMarkdownArticleForFroala(
                                      blogArticleAstToMarkdown(
                                          articlePost.contentAST,
                                          articlePost.photos,
                                          articlePost.contentASTAdditionalData,
                                      ),
                                  ),
                              articlePost.contentAST,
                          )
                        : this.astToHtmlCache(
                              () =>
                                  prepareHTMLArticleForFroala(
                                      blogArticleAstToHTML(
                                          articlePost.contentAST,
                                          articlePost.photos,
                                          articlePost.countTopicContributors,
                                          articlePost.contentASTAdditionalData,
                                      ),
                                  ),
                              articlePost.contentAST,
                          )
                    : null;

            let articlePhotos = [];
            if (articlePost && articlePost.photos) {
                articlePhotos = photoEntitiesToSuccessfulPhotoUploads(articlePost.photos);
            }

            return {
                ...mkReduxFormMapStateToProps(ARTICLE_CREATE_EDIT_FORM_NAME)(state),
                loadingState: articleEditState.loadingState,
                articlePost,
                showAds,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                baseUrl: getRequestBaseUrl(state),
                isMobile: getRequestDeviceMobile(state),
                isUploadingPhotos: isFormUploadingPhotos,
                isConvertingBody: articleEditState.isConvertingBody,
                isCreatingDraft: articleEditState.isCreatingDraft,
                disableEditorTypeChange: useOnlyMarkdownEditor,
                currentValues,
                initialValues:
                    articlePost &&
                    cachedInitialValues(
                        ARTICLE_CREATE_EDIT_FORM_NAME,
                        articlePost && {
                            title: getArticleTitleAsString(articlePost.titleAST),
                            body: articleContent,
                            bodyAST: articlePost.contentAST,
                            editorType: initialEditorType,
                            author: articlePost.user.username,
                            scripts: articlePost.scripts,
                            publishTime: clearPastPublishTime(articlePost.publishTime),
                            visibility: articlePost.visibility,
                            commentability: articlePost.commentability,
                            photos: articlePhotos,
                        },
                        articlePost,
                    ),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(ARTICLE_CREATE_EDIT_FORM_NAME),
            onLoad: articleEditFetchTrigger,
            onEditorTypeChange: articleEditChangeEditorTypeTrigger,
            onPrepareUploadPhotosCustom: articleEditInsertImageTrigger,
            onSaveAsDraft: (
                username: string,
                articleSlug: string,
                articleId: number,
                articleFd: ArticleFormData,
            ) =>
                articleEditSaveArticleTrigger(
                    username,
                    articleSlug,
                    articleId,
                    articleFd,
                    ArticleEditSaveActionType.SAVE_AS_DRAFT,
                ),
            onSaveAndPublish: (
                username: string,
                articleSlug: string,
                articleId: number,
                articleFd: ArticleFormData,
            ) =>
                articleEditSaveArticleTrigger(
                    username,
                    articleSlug,
                    articleId,
                    articleFd,
                    ArticleEditSaveActionType.SAVE_AND_PUBLISH,
                ),
            onArticleEditToggleMenuMore: () => menuToggle(BLOGS_ARTICLE_EDIT_MENU_MORE),
        };
    }

    public getMetaTitle(props: Props): string {
        const { articlePost } = props;

        return articlePost
            ? interpolate(SEO_TITLE_BLOGS_article, {
                  article: getArticleTitleAsString(articlePost.titleAST) || "",
              })
            : "";
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { articlePost } = props;
        if (articlePost) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...articleBlogsOG(baseUrl, articlePost),
            };
        }
    }

    public render(props: Props): false | JSX.Element {
        return <ArticleEdit {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { articleSlug, username },
        } = props;
        return url(blogsArticleUrl, { username, articleSlug });
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return null;
    }

    public getHeaderRight(props): React.ReactElement {
        const {
            onArticleEditToggleMenuMore,
            isUploadingPhotos,
            isConvertingBody,
            isCreatingDraft,
            articlePost,
        } = props;

        return (
            articlePost && (
                <>
                    <Menu
                        key="menu"
                        menuId={BLOGS_ARTICLE_EDIT_MENU_MORE}
                        trigger={
                            <Btn
                                type={BtnType.Blue}
                                icon={faCaretDown}
                                label={SAVE_AS}
                                onClick={onArticleEditToggleMenuMore}
                                layout={BtnLayout.LabelIcon}
                                hasSpinner={isCreatingDraft}
                            />
                        }
                    >
                        <MenuItem
                            label={SAVE_AS_DRAFT}
                            hasSpinner={isCreatingDraft}
                            icon={faFileEditRegular}
                            onClick={this.handleOnSaveAsDraft(props)}
                            disabled={isUploadingPhotos || isConvertingBody}
                            triggerAction={ARTICLE_EDIT_SAVE_ARTICLE_TRIGGER}
                        />
                        <MenuItem
                            label={SAVE_AS_PUBLISHED}
                            icon={faFileEditSolid}
                            hasSpinner={isCreatingDraft}
                            onClick={this.handleOnSaveAndPublish(props)}
                            disabled={
                                isUploadingPhotos || isConvertingBody
                                // || (articlePost.advancedArticle && IS_PROD)
                            }
                            triggerAction={ARTICLE_EDIT_SAVE_ARTICLE_TRIGGER}
                        />
                        {articlePost.advancedArticle && (
                            <MenuItem
                                label={"Prejdi na úpravy základných údajov receptu..."}
                                icon={faSoup}
                                link={editAdvancedArticleDetailsUrl(articlePost)}
                                disabled={isUploadingPhotos || isConvertingBody}
                            />
                        )}
                    </Menu>
                </>
            )
        );
    }

    public handleOnSaveAsDraft = (props: Props) => {
        const { articlePost, currentValues, onSaveAsDraft } = props;
        return () =>
            onSaveAsDraft(
                articlePost.user.username,
                articlePost.slug,
                articlePost.id,
                currentValues,
            );
    };

    public handleOnSaveAndPublish = (props: Props) => {
        const { articlePost, currentValues, onSaveAndPublish } = props;
        return () =>
            onSaveAndPublish(
                articlePost.user.username,
                articlePost.slug,
                articlePost.id,
                currentValues,
            );
    };

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.blogs.blogsArticleEdits", reducer);
        store.injectSaga("containers.blogs.blogsArticleEdits", saga);
    }
}
